import { BackdropLoading } from '@/components/backdrop-loading'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { Container, Content } from '@/components/container/container'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    AgendaBlock,
    getAgendaBlocksByAgenda,
} from '@/lib/api/agenda-blocks/agenda-blocks-api'
import {
    getAgendaEventsByProfessional,
    getAgendasByProfessional,
    getMyAgenda,
    getCompanyAgenda,
    getMyAgendaEvents,
    getCompanyAgendaEvents,
    getCompanyAgendaEventsByType,
} from '@/lib/api/agenda/agenda-api'
import {
    Agenda,
    AgendaEvent,
} from '@/lib/api/common/interfaces/agenda.interface'
import {
    WaitingListEntry,
    getWaitingListByAgenda,
} from '@/lib/api/waiting-list/waiting-list-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAdd,
    MdBiotech,
    MdBlock,
    MdBusiness,
    MdCalendarToday,
    MdChevronLeft,
    MdEventNote,
    MdHourglassEmpty,
    MdMedicalServices,
    MdPerson,
    MdSettings,
} from 'react-icons/md'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AddEventModal } from './add-event-modal'
import './calendar.css'
import { BlockPanel } from './components/blocks/BlockPanel'
import { CustomCalendar } from './components/calendar/CustomCalendar'
import { WaitingListPanel } from './components/waiting-list/WaitingListPanel'
import { EventDialog } from './event-dialog'
import { OverdueEventsModal } from './components/overdue/OverdueEventsModal'
import { CalendarView, getVisibleRange } from './utils/calendar-utils'

export type AgendaMode =
    | 'personal'
    | 'company'
    | 'professional'
    | 'orders'
    | 'consultations'
    | 'exams'

/** Modos que listam eventos por TIPO (não por profissional/empresa). Somente leitura. */
const TYPE_MODES: Record<string, 'PROCEDURE' | 'CONSULTATION' | 'EXAM'> = {
    orders: 'PROCEDURE',
    consultations: 'CONSULTATION',
    exams: 'EXAM',
}

export default function AgendaPage() {
    const { user, isProfessional } = useAuth()
    const serviceLabels = useServiceLabel()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const showOverdue = searchParams.get('showOverdue') === 'true'
    const isHealthSegment = user?.company?.segment === 'HEALTH'
    const [agendaMode, setAgendaMode] = useState<AgendaMode>('personal')
    const [events, setEvents] = useState<AgendaEvent[]>([])
    const [selectedEvent, setSelectedEvent] = useState<AgendaEvent | null>(null)
    const [professionalId, setProfessionalId] = useState<string>('')
    const [currentAgenda, setCurrentAgenda] = useState<Agenda | null>(null)
    const [loading, setLoading] = useState(false)
    const [currentDate, setCurrentDate] = useState<Date>(new Date())
    const [view, setView] = useState<CalendarView>('month')
    const [showAddModal, setShowAddModal] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Date | undefined>()

    // Blocks and Waiting List
    const [blocks, setBlocks] = useState<AgendaBlock[]>([])
    const [waitingList, setWaitingList] = useState<WaitingListEntry[]>([])
    const [showBlocksPanel, setShowBlocksPanel] = useState(false)
    const [showWaitingListPanel, setShowWaitingListPanel] = useState(false)

    const isProfessionalMode = agendaMode === 'professional'
    const isTypeMode = agendaMode in TYPE_MODES

    useEffect(() => {
        if (user?.companyId && isProfessionalMode) {
            if (isProfessional && user.professionalId) {
                setProfessionalId(user.professionalId.toString())
            } else {
                const savedId = localStorage.getItem('professional_id')
                if (savedId) {
                    setProfessionalId(savedId)
                }
            }
        }
    }, [user?.companyId, agendaMode])

    useEffect(() => {
        if (isProfessionalMode) {
            if (professionalId) {
                loadAgenda()
                loadEvents()
            } else {
                setEvents([])
                setCurrentAgenda(null)
                setBlocks([])
                setWaitingList([])
            }
        }
    }, [professionalId, currentDate, view, agendaMode])

    useEffect(() => {
        if (!isProfessionalMode) {
            loadAgenda()
            loadEvents()
        }
    }, [agendaMode, currentDate, view])

    useEffect(() => {
        if (currentAgenda?.id) {
            loadBlocks()
            loadWaitingList()
        }
    }, [currentAgenda?.id])

    const loadAgenda = async () => {
        // Abas por tipo agregam eventos de várias agendas — não há uma agenda única
        // para bloqueios/lista de espera/configurações.
        if (isTypeMode) {
            setCurrentAgenda(null)
            setBlocks([])
            setWaitingList([])
            return
        }

        if (agendaMode === 'personal') {
            try {
                const response = await getMyAgenda()
                if (response.success && response.result) {
                    setCurrentAgenda(response.result)
                } else {
                    setCurrentAgenda(null)
                }
            } catch (error) {
                console.error('Erro ao carregar agenda pessoal:', error)
                setCurrentAgenda(null)
            }
            return
        }

        if (agendaMode === 'company') {
            try {
                const response = await getCompanyAgenda()
                if (response.success && response.result) {
                    setCurrentAgenda(response.result)
                } else {
                    setCurrentAgenda(null)
                }
            } catch (error) {
                console.error('Erro ao carregar agenda da empresa:', error)
                setCurrentAgenda(null)
            }
            return
        }

        if (!professionalId) return

        try {
            const response = await getAgendasByProfessional(
                parseInt(professionalId)
            )
            if (response.success && response.result) {
                setCurrentAgenda(response.result)
            } else {
                setCurrentAgenda(null)
            }
        } catch (error) {
            console.error('Erro ao carregar agenda:', error)
            setCurrentAgenda(null)
        }
    }

    const loadEvents = async () => {
        // Só o período que a tela mostra. O servidor devolvia todos os eventos de
        // todos os anos e a tela descartava o que estava fora do mês — com os
        // atendimentos importados na agenda isso passaria de dezenas de milhares
        // de registros por abertura.
        const range = getVisibleRange(currentDate, view)

        if (isTypeMode) {
            setLoading(true)
            try {
                const response = await getCompanyAgendaEventsByType(
                    TYPE_MODES[agendaMode],
                    range
                )
                setEvents(response.success ? response.result : [])
            } catch {
                setEvents([])
            } finally {
                setLoading(false)
            }
            return
        }

        if (agendaMode === 'personal') {
            setLoading(true)
            try {
                const response = await getMyAgendaEvents(range)
                if (response.success) {
                    setEvents(response.result)
                } else {
                    setEvents([])
                }
            } catch (error) {
                setEvents([])
            } finally {
                setLoading(false)
            }
            return
        }

        if (agendaMode === 'company') {
            setLoading(true)
            try {
                const response = await getCompanyAgendaEvents(range)
                if (response.success) {
                    setEvents(response.result)
                } else {
                    setEvents([])
                }
            } catch (error) {
                setEvents([])
            } finally {
                setLoading(false)
            }
            return
        }

        if (!professionalId) return

        setLoading(true)
        try {
            const eventsResponse = await getAgendaEventsByProfessional(
                parseInt(professionalId),
                range
            )

            if (eventsResponse.success) {
                setEvents(eventsResponse.result)
            } else {
                toast.error(eventsResponse.error || 'Erro ao carregar eventos')
                setEvents([])
            }
        } catch (error) {
            toast.error('Erro ao carregar eventos')
            setEvents([])
        } finally {
            setLoading(false)
        }
    }

    const loadBlocks = async () => {
        if (!currentAgenda?.id) return

        try {
            const response = await getAgendaBlocksByAgenda(currentAgenda.id)
            if (response.success) {
                setBlocks(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar bloqueios:', error)
        }
    }

    const loadWaitingList = async () => {
        if (!currentAgenda?.id) return

        try {
            const response = await getWaitingListByAgenda(currentAgenda.id)
            if (response.success) {
                setWaitingList(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar lista de espera:', error)
        }
    }

    const handleSetProfessional = (id: string) => {
        setProfessionalId(id)
        localStorage.setItem('professional_id', id)
    }

    const handleModeChange = (mode: AgendaMode) => {
        setAgendaMode(mode)
        setEvents([])
        setCurrentAgenda(null)
        setBlocks([])
        setWaitingList([])
        setShowBlocksPanel(false)
        setShowWaitingListPanel(false)
    }

    const handleSelectEvent = (event: AgendaEvent) => {
        setSelectedEvent(event)
    }

    const handleSlotClick = (date: Date) => {
        // Abas por tipo são somente leitura: agendar é feito nas telas dedicadas.
        if (isTypeMode) {
            return
        }

        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (date < today) {
            return
        }

        if (isProfessionalMode && !professionalId) {
            toast.error('Selecione um profissional primeiro')
            return
        }

        // Clique no dia (visão mensal) chega com 00:00. Sugere a próxima hora
        // cheia em vez de meia-noite para facilitar a criação do evento.
        const slot = new Date(date)
        if (slot.getHours() === 0 && slot.getMinutes() === 0) {
            const now = new Date()
            const nextHour =
                now.getMinutes() === 0 ? now.getHours() : now.getHours() + 1
            slot.setHours(Math.min(nextHour, 23), 0, 0, 0)
        }

        setSelectedDate(slot)
        setShowAddModal(true)
    }

    const handleDateChange = (newDate: Date) => {
        setCurrentDate(newDate)
    }

    const handleViewChange = (newView: CalendarView) => {
        setView(newView)
    }

    const handleScheduleFromWaitingList = useCallback(
        (entry: WaitingListEntry) => {
            if (isProfessionalMode && !professionalId) {
                toast.error('Selecione um profissional primeiro')
                return
            }
            setSelectedDate(new Date())
            setShowAddModal(true)
        },
        [professionalId, agendaMode]
    )

    const refreshAll = useCallback(() => {
        loadEvents()
        if (currentAgenda?.id) {
            loadBlocks()
            loadWaitingList()
        }
    }, [currentAgenda?.id, professionalId, agendaMode])

    const waitingCount = waitingList.filter(e => e.status === 'WAITING').length
    const activeBlocksCount = blocks.filter(b => {
        const now = new Date()
        const start = new Date(b.startDate)
        const end = new Date(b.endDate)
        return start <= now && end >= now
    }).length

    const canShowCalendar = isProfessionalMode ? !!professionalId : true

    return (
        <Container>
            {/* Header */}
            <div className="mb-5">
                {/* Mode tabs */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                        className={`btn btn-sm ${agendaMode === 'personal' ? 'btn-secondary' : 'btn-ghost'}`}
                        onClick={() => handleModeChange('personal')}
                    >
                        <MdPerson className="text-lg" />
                        Pessoal
                    </button>
                    <button
                        className={`btn btn-sm ${agendaMode === 'company' ? 'btn-secondary' : 'btn-ghost'}`}
                        onClick={() => handleModeChange('company')}
                    >
                        <MdBusiness className="text-lg" />
                        Empresa
                    </button>
                    {isHealthSegment && (
                        <>
                            <button
                                className={`btn btn-sm ${agendaMode === 'professional' ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => handleModeChange('professional')}
                            >
                                <MdCalendarToday className="text-lg" />
                                Médicos
                            </button>
                            <button
                                className={`btn btn-sm ${agendaMode === 'orders' ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => handleModeChange('orders')}
                            >
                                <MdMedicalServices className="text-lg" />
                                {serviceLabels.Orders}
                            </button>
                            <button
                                className={`btn btn-sm ${agendaMode === 'consultations' ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => handleModeChange('consultations')}
                            >
                                <MdEventNote className="text-lg" />
                                {serviceLabels.Consultations}
                            </button>
                            <button
                                className={`btn btn-sm ${agendaMode === 'exams' ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => handleModeChange('exams')}
                            >
                                <MdBiotech className="text-lg" />
                                Exames
                            </button>
                        </>
                    )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* Professional selector - only in professional mode */}
                    {isProfessionalMode && !isProfessional && (
                        <div className="min-w-[280px]">
                            <ProfessionalSearchSelector
                                value={
                                    professionalId
                                        ? Number(professionalId)
                                        : null
                                }
                                onChange={id =>
                                    handleSetProfessional(id ? String(id) : '')
                                }
                                label=""
                                placeholder="Selecione um profissional..."
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-auto">
                        {currentAgenda && (
                            <>
                                <button
                                    className={`btn btn-sm ${showBlocksPanel ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() =>
                                        setShowBlocksPanel(!showBlocksPanel)
                                    }
                                    title="Bloqueios de horario"
                                >
                                    <MdBlock className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Bloqueios
                                    </span>
                                    {activeBlocksCount > 0 && (
                                        <span className="badge badge-xs badge-warning">
                                            {activeBlocksCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    className={`btn btn-sm ${showWaitingListPanel ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() =>
                                        setShowWaitingListPanel(
                                            !showWaitingListPanel
                                        )
                                    }
                                    title="Lista de espera"
                                >
                                    <MdHourglassEmpty className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Espera
                                    </span>
                                    {waitingCount > 0 && (
                                        <span className="badge badge-xs badge-warning">
                                            {waitingCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() =>
                                        navigate(
                                            `/atendimento/agenda/${currentAgenda.id}/settings`
                                        )
                                    }
                                    title="Configuracoes"
                                >
                                    <MdSettings className="text-lg" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Main content area */}
            <div className="flex gap-4">
                {/* Calendar */}
                <Content className="flex-1 p-0 shadow-2xl bg-white rounded-xl relative min-h-[600px]">
                    {canShowCalendar ? (
                        <CustomCalendar
                            events={events}
                            initialDate={currentDate}
                            initialView={view}
                            workingHours={
                                currentAgenda?.workingHours
                                    ? { start: 8, end: 18 }
                                    : { start: 8, end: 18 }
                            }
                            slotDuration={currentAgenda?.slotDuration || 30}
                            onDateChange={handleDateChange}
                            onViewChange={handleViewChange}
                            onSlotClick={handleSlotClick}
                            onEventClick={handleSelectEvent}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-base-content/60">
                            <MdPerson className="text-6xl mb-4 opacity-50" />
                            {isProfessional ? (
                                <>
                                    <p className="text-lg font-medium">
                                        Carregando sua agenda...
                                    </p>
                                    <p className="text-sm mt-1">
                                        Aguarde enquanto carregamos seus dados
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-lg font-medium">
                                        Selecione um profissional
                                    </p>
                                    <p className="text-sm mt-1">
                                        Clique em um profissional acima ou
                                        busque pelo nome
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </Content>

                {/* Side panels */}
                {currentAgenda && (showBlocksPanel || showWaitingListPanel) && (
                    <div className="hidden lg:flex flex-col gap-4 w-80">
                        {showBlocksPanel && (
                            <BlockPanel
                                agendaId={currentAgenda.id}
                                blocks={blocks}
                                onRefresh={loadBlocks}
                                collapsible={false}
                            />
                        )}
                        {showWaitingListPanel && (
                            <WaitingListPanel
                                agendaId={currentAgenda.id}
                                entries={waitingList}
                                onRefresh={loadWaitingList}
                                onSchedule={handleScheduleFromWaitingList}
                                collapsible={false}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Mobile side panels (bottom sheet style) */}
            {currentAgenda && (
                <>
                    {/* Mobile Blocks Panel */}
                    <div
                        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 transform transition-transform duration-300 ${
                            showBlocksPanel
                                ? 'translate-y-0'
                                : 'translate-y-full'
                        }`}
                    >
                        <div className="bg-base-100 rounded-t-2xl shadow-2xl max-h-[70vh] overflow-y-auto">
                            <div className="sticky top-0 bg-base-100 p-4 border-b border-base-300 flex items-center justify-between">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <MdBlock className="text-xl" />
                                    Bloqueios de Horario
                                </h3>
                                <button
                                    className="btn btn-sm btn-ghost btn-circle"
                                    onClick={() => setShowBlocksPanel(false)}
                                >
                                    <MdChevronLeft className="text-xl rotate-90" />
                                </button>
                            </div>
                            <div className="p-4">
                                <BlockPanel
                                    agendaId={currentAgenda.id}
                                    blocks={blocks}
                                    onRefresh={loadBlocks}
                                    collapsible={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Mobile Waiting List Panel */}
                    <div
                        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 transform transition-transform duration-300 ${
                            showWaitingListPanel
                                ? 'translate-y-0'
                                : 'translate-y-full'
                        }`}
                    >
                        <div className="bg-base-100 rounded-t-2xl shadow-2xl max-h-[70vh] overflow-y-auto">
                            <div className="sticky top-0 bg-base-100 p-4 border-b border-base-300 flex items-center justify-between">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <MdHourglassEmpty className="text-xl text-amber-500" />
                                    Lista de Espera
                                </h3>
                                <button
                                    className="btn btn-sm btn-ghost btn-circle"
                                    onClick={() =>
                                        setShowWaitingListPanel(false)
                                    }
                                >
                                    <MdChevronLeft className="text-xl rotate-90" />
                                </button>
                            </div>
                            <div className="p-4">
                                <WaitingListPanel
                                    agendaId={currentAgenda.id}
                                    entries={waitingList}
                                    onRefresh={loadWaitingList}
                                    onSchedule={handleScheduleFromWaitingList}
                                    collapsible={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Mobile backdrop */}
                    {(showBlocksPanel || showWaitingListPanel) && (
                        <div
                            className="lg:hidden fixed inset-0 bg-black/50 z-30"
                            onClick={() => {
                                setShowBlocksPanel(false)
                                setShowWaitingListPanel(false)
                            }}
                        />
                    )}
                </>
            )}

            {/* FAB for mobile — some nas abas por tipo (somente leitura) */}
            {!isTypeMode && (
                <button
                    className="btn btn-circle btn-secondary fixed bottom-6 right-6 z-50 shadow-lg md:hidden"
                    onClick={() => {
                        if (isProfessionalMode && !professionalId) {
                            toast.error('Selecione um profissional primeiro')
                            return
                        }
                        setSelectedDate(new Date())
                        setShowAddModal(true)
                    }}
                    style={{ position: 'fixed' }}
                    aria-label="Adicionar evento"
                >
                    <MdAdd className="text-2xl" />
                </button>
            )}

            {/* Modals */}
            {showAddModal && (
                <AddEventModal
                    date={selectedDate}
                    professionalId={professionalId}
                    agendaMode={agendaMode}
                    agendaId={currentAgenda?.id}
                    onClose={() => {
                        setShowAddModal(false)
                        setSelectedDate(undefined)
                    }}
                    onSuccess={() => {
                        loadEvents()
                    }}
                />
            )}

            {selectedEvent && (
                <EventDialog
                    event={selectedEvent}
                    onClose={reload => {
                        setSelectedEvent(null)
                        if (reload) {
                            loadEvents()
                        }
                    }}
                />
            )}

            <OverdueEventsModal
                forceOpen={showOverdue}
                onForceOpenHandled={() => {
                    setSearchParams({}, { replace: true })
                }}
                onEventResolved={refreshAll}
            />

            <BackdropLoading visible={loading} />
        </Container>
    )
}
